import { useLayoutEffect, useRef } from "react";
import { initializePixiApp } from "../pixi-components/pixiApp";
import {
  loadViewportEventHandlers,
  unloadViewportEventHandlers,
  updateResolution
} from "../pixi-components/layers/viewport/viewport.events";
import { backgroundGrid } from "../pixi-components/layers/background/background.graphics";
import { updateBackground } from "../pixi-components/layers/background/background.utils";
import {
  initializeCulling,
  uninitializeCulling
} from "../pixi-components/layers/viewport/culling";
import {
  debugLayer,
  dragLayer,
  lineLayer,
  mainLayer
} from "../pixi-components/mainContainer/mainContainer";
import { initializeViewport } from "../pixi-components/layers/viewport/viewport.layer";
import { devLogger } from "@certa/common/utils/devLogger.ts";

/**
 * Disabled wheel event to make pinch-to-zoom work when in WebGL mode
 * @param event
 */
const disableWheel = (event: MouseEvent) => {
  event.preventDefault();
};

/**
 * Initializes the Pixi App and returns the stage container
 * reference which can be bound to a div element.
 * @returns
 */
export const usePixiInitializer = () => {
  const stageContainerRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!stageContainerRef.current) {
      devLogger.log("App not initialized");
      return;
    }

    // capturing the current reference as a constant so it stays the same
    // during "unmount"
    const currentStageContainer = stageContainerRef.current;

    // Initialize the app
    const pixiApp = initializePixiApp(currentStageContainer);
    pixiApp.start();

    // Initialize the viewport
    const { viewport, cleanup } = initializeViewport(pixiApp);

    // Add background grid and viewport to stage
    pixiApp.stage.addChild(backgroundGrid);
    pixiApp.stage.addChild(viewport);

    // Add main layer to viewport
    viewport.addChild(lineLayer);
    viewport.addChild(mainLayer);
    viewport.addChild(dragLayer);
    viewport.addChild(debugLayer);

    // Initialize culling
    initializeCulling();

    // Redraw background on initial load
    updateBackground();

    // This is required to make "pinch to zoom in/out" work.
    // Relevant threads:
    // - https://github.com/pixijs/pixijs/issues/6414#issuecomment-586594577
    // - https://github.com/pixijs/pixijs/wiki/v5-Hacks#prevent-pinch-gesture-in-chrome
    currentStageContainer.addEventListener("wheel", disableWheel, {
      passive: false
    });

    /**
     * Essentially preventing users from selecting text within that element or its descendants.
     * In our case, a canvas where you don't want the default text selection behavior to occur.
     * Why? Because when you expand or collapse the comps the header elements were getting auto selected.
     *
     * **/
    currentStageContainer.style.userSelect = "none";

    // Load viewport event handlers
    loadViewportEventHandlers();

    // Variable that will hold the remove listener function
    // for the current pixel ratio change listener
    let removePixelRatioUpdateListener: (() => void) | null = null;

    const updatePixelRatio = () => {
      // Initially removePixelRatioUpdateListener will be null
      // But all subsequent times it will be a function
      if (removePixelRatioUpdateListener != null) {
        // Remove the last listener, because it was for the previous pixel ratio
        // and we need to add a new listener for the new pixel ratio
        removePixelRatioUpdateListener();
      }

      // Create a media query string to match the current device pixel ratio
      const mqString = `(resolution: ${window.devicePixelRatio}dppx)`;

      // Create a media query list
      const media = matchMedia(mqString);

      // Add a listener to the media query list, for "change" event
      // which will trigger whenever current pixel ratio changes to something else
      media.addEventListener("change", updatePixelRatio);

      // Set the removePixelRatioUpdateListener to the function that removes the listener
      removePixelRatioUpdateListener = () => {
        media.removeEventListener("change", updatePixelRatio);
      };

      // Set the renderer resolution to the current device pixel ratio
      devLogger.log("Setting pixel ratio to " + window.devicePixelRatio);

      // Set the renderer resolution to the current device pixel ratio
      pixiApp.renderer.resolution = window.devicePixelRatio;

      // Update the resolution of the viewport
      updateResolution();
    };

    // Call the updatePixelRatio function to set the initial pixel ratio
    updatePixelRatio();

    devLogger.log("App initialized and started");
    return () => {
      devLogger.log("Stopping App");
      currentStageContainer.removeEventListener("wheel", disableWheel);

      uninitializeCulling();
      viewport.removeChildren();
      pixiApp.stage.removeChild(viewport);
      pixiApp.stage.removeChild(backgroundGrid);
      // Unload viewport event handlers on unmount
      unloadViewportEventHandlers();
      cleanup(); // destroys the viewport
      // Stop the app on unmount
      pixiApp?.stop();
      pixiApp?.destroy();
      removePixelRatioUpdateListener?.();
    };
  }, []);

  return {
    stageContainerRef
  };
};
